import React from 'react';
import { ShieldAlert, ChevronRight } from 'lucide-react';

interface CardItem {
  number: string;
  title: string;
  description: string;
  link: string;
}

export const AdminControlSafeFallback: React.FC = () => {
  const cards: CardItem[] = [
    { number: '01', title: 'Remote Workforce OS', description: 'Overwatch control plane for directing sandboxed agent activities.', link: '/operator-console?view=director' },
    { number: '02', title: 'Worker Verification Centre', description: 'Compliance review and identity clearance console.', link: '/operator-console?view=verification' },
    { number: '03', title: 'Worker Directory', description: 'Registry of all active operational nodes and credentials.', link: '/operator-console?view=directory' },
    { number: '04', title: 'Production Hub', description: 'Real-time task allocations and build status monitors.', link: '/operator-console?view=production' },
    { number: '05', title: 'Campaign Dispatch Console', description: 'Scheduler and manager for outbound client campaigns.', link: '/admin-control?view=campaign-dispatch' },
    { number: '06', title: 'Campaign Traffic Tracker', description: 'UTM link generator and analytics tracker.', link: '/operator-console?view=traffic' },
    { number: '07', title: 'Campaign Asset Gallery', description: 'Media bank and brand assets resource manager.', link: '/admin-control?view=campaign-assets' },
    { number: '08', title: 'Social API Connectors', description: 'Integrations and tokens dashboard for API platforms.', link: '/admin-control?view=social-connectors' },
    { number: '09', title: 'Lead Queue Manager', description: 'Pipeline monitor for marketing leads and CRM imports.', link: '/operator-console?view=workbench' },
    { number: '10', title: 'Product Research Queue', description: 'Trend discovery and product analyzer database.', link: '/operator-console?view=workbench&subtab=productQueue' },
    { number: '11', title: 'Commission Approvals', description: 'Financial clearance for operator payouts.', link: '/operator-console?view=director&subtab=commissions' },
    { number: '12', title: 'Workspace Audit Logs', description: 'Immutable ledger entries of system state changes.', link: '/operator-console?view=director&subtab=security' },
    { number: '13', title: 'Run Isolation Security Test', description: 'Trigger virtual environment containment validation checks.', link: '/operator-console?view=director-security' },
    { number: '14', title: 'Operator Invite Manager', description: 'Access token dispatch control for new operators.', link: '/operator-console?view=director&subtab=invites' },
    { number: '15', title: 'SMTP / Email Test Centre', description: 'Internal testing suite for outgoing client correspondence.', link: '/operator-console?view=director&subtab=smtp' },
    { number: '16', title: 'Legacy / Internal Lab', description: 'Developer testing playground. (Director Privilege Required).', link: '/operator-console?view=director-lab' }
  ];

  return (
    <div className="min-h-screen bg-black text-white font-sans p-8 flex flex-col items-center justify-start selection:bg-purple-500 selection:text-white">
      {/* Notice Banner */}
      <div className="w-full max-w-6xl mb-8 p-4 bg-purple-950/20 border border-purple-500/30 rounded-2xl flex items-center gap-3 shadow-[0_0_20px_rgba(168,85,247,0.15)] animate-pulse">
        <ShieldAlert className="text-purple-400 w-5 h-5 flex-shrink-0" />
        <span className="text-xs font-mono text-purple-300">
          Safe Admin Fallback Mode Active — original dashboard temporarily bypassed after runtime crash.
        </span>
      </div>

      <div className="w-full max-w-6xl space-y-6">
        <div className="border-b border-zinc-900 pb-5">
          <h1 className="text-3xl font-black uppercase tracking-tight bg-gradient-to-r from-white via-slate-200 to-purple-400 bg-clip-text text-transparent">
            Admin Control Centre
          </h1>
          <p className="text-xs text-slate-500 uppercase font-mono tracking-widest pt-1">
            NexoraX Product Factory / Overwatch Gatekeeper
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {cards.map((card) => (
            <div 
              key={card.number} 
              className="group relative bg-zinc-950 border border-zinc-900 hover:border-purple-500/40 rounded-2xl p-5 transition-all duration-300 hover:shadow-[0_0_25px_rgba(168,85,247,0.1)] flex flex-col justify-between min-h-[160px]"
            >
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-mono font-black text-purple-500 bg-purple-500/10 px-2 py-0.5 rounded">
                    {card.number}
                  </span>
                  <span className="text-[9px] font-mono text-zinc-600 uppercase tracking-widest">
                    Secure Node
                  </span>
                </div>
                <h3 className="text-sm font-black text-white group-hover:text-purple-300 transition-colors">
                  {card.title}
                </h3>
                <p className="text-xs text-slate-400 leading-relaxed font-medium">
                  {card.description}
                </p>
              </div>
              
              <div className="pt-4">
                <a 
                  href={card.link}
                  className="inline-flex items-center gap-1 text-[10px] font-mono font-black uppercase text-purple-400 hover:text-purple-300 transition-colors"
                >
                  <span>Launch Control</span>
                  <ChevronRight size={10} className="transform group-hover:translate-x-0.5 transition-transform" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
